import { ChangeEvent, FunctionComponent, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ClickAwayListener, OutlinedInput, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useFieldStyles } from 'components/UI/FieldStyles';
import LabelField from 'components/UI/LabelField';
import { faChevronDown, faChevronUp, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { Product } from '@spike/product-model';

export interface SelectProductProps {
    id?: string;
    label?: string | JSX.Element;
    name?: string;
    selectedOption?: Product;
    placeholder?: string;
    options: Array<Product>;
    disabled?: boolean;
    className?: string;
    required?: boolean;
    information?: string;
    onSelect: (option: Product) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            minWidth: 110
        },
        dropdownContainer: {
            position: 'relative',
            height: 54
        },
        collapsedContainer: {
            height: '100%'
        },
        expandedContainer: {
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10,
            width: '100%',
            backgroundColor: 'white',
            border: '1px solid #222222 !important',
            paddingBottom: 30,
            [theme.breakpoints.down('md')]: {
                paddingBottom: 33
            }
        },
        headerContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            height: 49,
            cursor: 'pointer'
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            color: '#000000',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            fontSize: '16px',
            lineHeight: '27px',
            paddingLeft: 28,
            width: 290,
            [theme.breakpoints.up('md')]: {
                paddingLeft: 30,
                width: 420
            }
        },
        listContainer: {
            borderTop: '1px solid #222222',
            overflowY: 'auto',
            height: 'auto',
            maxHeight: 200,
            paddingTop: 12,
            [theme.breakpoints.up('md')]: {
                paddingTop: 14
            }
        },
        text: {
            color: '#000000',
            fontSize: 16,
            lineHeight: '27px',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
        },
        placeholder: {
            color: '#7A7A7A',
            paddingLeft: 18,
            [theme.breakpoints.up('md')]: {
                paddingLeft: 20
            }
        },
        border: {
            border: '1px solid #222222',
            borderRadius: 27,
            [theme.breakpoints.up('md')]: {
                borderRadius: 30
            }
        },
        textContainer: {
            alignItems: 'center',
            paddingRight: 10,
            width: '85%'
        },
        chevronContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            paddingRight: 32,
            [theme.breakpoints.up('md')]: {
                paddingRight: 35
            }
        },
        itemContainer: {
            'display': 'flex',
            'justifyContent': 'space-between',
            'height': 45,
            '&:hover': {
                background: '#F8F8F8'
            }
        },
        itemContent: {
            display: 'flex',
            width: '75%',
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 20,
            paddingRight: 20,
            cursor: 'pointer'
        },
        itemContentSelected: {
            paddingLeft: 18,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            alignItems: 'center',
            marginRight: 4,
            width: '95%',
            [theme.breakpoints.up('md')]: {
                paddingLeft: 20
            }
        },
        item: {
            fontSize: 16,
            lineHeight: '16px'
        },
        optionNotSelected: {
            border: '1px solid #D3D3D3 !important'
        },
        chevron: {
            fontSize: 16
        },
        bold: {
            fontWeight: 500
        },
        sectionProduct: {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            alignItems: 'center',
            marginRight: 4,
            height: 17,
            width: '100%',
            [theme.breakpoints.up('md')]: {
                marginRight: 5
            }
        },
        productNameContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        },
        productItemContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            marginRight: 10
        },
        spacing: {
            marginLeft: 4,
            marginRight: 10,
            [theme.breakpoints.up('md')]: {
                marginLeft: 5
            }
        },
        searchBox: {
            'height': 47,
            'width': '100%',
            'fontSize': 16,
            'border': '1px solid #000000',
            'borderTop': 'none',
            'borderRight': 'none',
            'borderLeft': 'none',
            'borderRadius': 0,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1px !important',
                borderColor: '#00000 !important',
                borderRadius: 0
            },
            '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none !important'
            },
            '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0) !important'
            },
            '&.MuiOutlinedInput-adornedStart': {
                paddingLeft: '0px !important'
            }
        },
        startIcon: {
            marginRight: 7
        },
        searchBoxContainer: {
            paddingLeft: 20,
            paddingRight: 20,
            width: '100%',
            paddingBottom: 13
        },
        noResults: {
            paddingLeft: 20,
            paddingTop: 13
        }
    })
);

export const SelectStaff: FunctionComponent<SelectProductProps> = props => {
    const classes = useStyles();
    const fieldClasses = useFieldStyles();

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [options, setOptions] = useState(props.options);

    const clickCollapseHandler = () => {
        setIsCollapsed(prev => !prev);
    };

    const changeHandler = (idSelected: number) => {
        props.onSelect(props.options.find(option => option.businessProduct?.id === idSelected)!);
    };

    useEffect(() => {
        if (searchText) {
            const filteredOptions = props.options.filter(option =>
                option.businessProduct?.name.toLowerCase().includes(searchText.toLowerCase())
            );
            setOptions(filteredOptions);
        } else {
            setOptions(props.options);
        }
    }, [searchText]);

    useEffect(() => {
        if (isCollapsed) {
            setSearchText('');
        }
    }, [isCollapsed]);

    const header = (
        <Box className={classes.headerContainer}>
            <Box className={classes.textContainer}>
                {props.selectedOption ? (
                    <Box className={clsx(classes.itemContent, classes.itemContentSelected, classes.text)}>
                        <Typography className={classes.text}>{props.selectedOption.businessProduct?.name}</Typography>
                    </Box>
                ) : (
                    <Typography className={clsx(classes.placeholder, classes.text)}>{props.placeholder}</Typography>
                )}
            </Box>
            <Box className={classes.chevronContainer}>
                <FontAwesomeIcon
                    icon={isCollapsed ? faChevronDown : faChevronUp}
                    color={isCollapsed ? '#7A7A7A' : '#222222'}
                    className={classes.chevron}
                />
            </Box>
        </Box>
    );

    const searchBox = (
        <Box
            onClick={e => e.stopPropagation()}
            className={classes.searchBoxContainer}
        >
            <OutlinedInput
                className={classes.searchBox}
                value={searchText}
                placeholder=""
                startAdornment={
                    <FontAwesomeIcon
                        icon={faSearch}
                        className={classes.startIcon}
                    />
                }
                onChange={(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                    setSearchText(event.target.value)
                }
                id={props.id}
            />
        </Box>
    );

    const list = (
        <Box className={classes.listContainer}>
            {searchBox}
            {options.map((opt, index) => (
                <Box
                    className={classes.itemContainer}
                    key={opt.businessProduct?.id}
                    id={`booking_option_product_${index}`}
                    onClick={() => changeHandler(opt.businessProduct?.id!)}
                >
                    <Box className={classes.itemContent}>
                        <Box className={classes.productNameContainer}>
                            <Typography className={clsx(classes.item, classes.sectionProduct)}>
                                {opt?.businessProduct?.name}
                            </Typography>
                        </Box>
                    </Box>
                    <Box className={classes.productItemContainer}>
                        <Typography className={clsx(classes.item, classes.bold, classes.spacing)}>
                            ${Number(opt?.businessProduct?.pricing.price?.toString())?.toFixed(2)}
                        </Typography>
                    </Box>
                </Box>
            ))}
            {options.length === 0 && searchText.length > 0 && (
                <Typography className={clsx(classes.item, classes.text, classes.noResults)}>
                    No results found
                </Typography>
            )}
        </Box>
    );

    const collapsed = (
        <Box
            className={clsx(classes.border, classes.collapsedContainer, [classes.optionNotSelected])}
            onClick={clickCollapseHandler}
        >
            {header}
        </Box>
    );

    const expanded = (
        <Box
            className={clsx(classes.border, classes.expandedContainer)}
            onClick={clickCollapseHandler}
        >
            {header}
            {list}
        </Box>
    );

    return (
        <ClickAwayListener onClickAway={() => setIsCollapsed(true)}>
            <Box className={clsx(classes.container, fieldClasses.container, props.className)}>
                <LabelField {...props} />
                <Box
                    className={classes.dropdownContainer}
                    id="booking_list_products"
                >
                    {isCollapsed ? collapsed : expanded}
                </Box>
            </Box>
        </ClickAwayListener>
    );
};

export default SelectStaff;
